import { useEffect, useMemo, useRef } from 'react';
import { clsx } from 'clsx';
import { Btn } from '../ui';
import type { tMediaInput } from '../../types';
import type { Key } from 'react-indexed-db-hook/lib/indexed-db';

type InspectFlowWarnInopDetailsPlacement = 'embedded' | 'standalone';

export type InspectFlowWarnInopDetailsProps = {
    tone: 'warn' | 'inop';
    placement?: InspectFlowWarnInopDetailsPlacement;
    notes: string;
    onNotesChange: (notes: string) => void;
    mediaItems: tMediaInput[];
    audioBlob?: Blob;
    recorderStatus: string;
    onFilesSelected: (files: FileList) => void;
    onStartRecording: () => void;
    onStopRecording: () => void;
    onRemoveAudio: () => void;
    onRemoveMediaItem: (item: tMediaInput) => void;
    heading?: string;
    notesVisibleLabel?: string;
    id?: string;
};

const PhotoVideoIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="inspect-flow-media-btn-icon">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.75" />
        <circle cx="8.5" cy="10" r="1.5" fill="currentColor" />
        <path d="M3 16l4.5-4 3 2.5L14 12l7 7" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
    </svg>
);

const MicrophoneIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="inspect-flow-media-btn-icon">
        <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.75" />
        <path d="M6 11a6 6 0 0 0 12 0M12 17v4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
);

const isRecordingStopped = (status: string) => status === 'stopped' || status === 'idle';

export const InspectFlowWarnInopDetails = ({
    tone,
    placement = 'standalone',
    notes,
    onNotesChange,
    mediaItems,
    audioBlob,
    recorderStatus,
    onFilesSelected,
    onStartRecording,
    onStopRecording,
    onRemoveAudio,
    onRemoveMediaItem,
    heading,
    notesVisibleLabel,
    id,
}: InspectFlowWarnInopDetailsProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const fileInputId = `inspect-flow-media-file-input-${placement}`;
    const detailsLabel = heading || (tone === 'warn' ? 'Warning details (required)' : 'Inoperative details (required)');
    const isRecording = recorderStatus === 'recording';
    const isStarting = recorderStatus === 'acquiring_media';
    const audioUrl = useMemo(() => (audioBlob ? URL.createObjectURL(audioBlob) : null), [audioBlob]);

    useEffect(() => {
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, [audioUrl]);

    const photoVideoItems = mediaItems.filter((item) => item.name !== 'audio.wav' && item.data?.file);

    return (
        <section
            id={id}
            className={clsx(
                'inspect-flow-warn-inop-details',
                `tone-${tone}`,
                placement === 'embedded' && 'inspect-flow-warn-inop-details--embedded',
                placement === 'standalone' && 'inspect-flow-warn-inop-details--standalone',
            )}
            aria-label={detailsLabel}
        >
            <h3 className="inspect-flow-warn-inop-details-title">
                <span className="inspect-flow-warn-inop-details-icon" aria-hidden="true">
                    {tone === 'warn' ? '⚠' : '✕'}
                </span>
                {detailsLabel}
            </h3>

            <label className="inspect-flow-warn-inop-notes-field">
                <span className={notesVisibleLabel ? 'inspect-flow-wizard-field-label' : 'visually-hidden'}>
                    {notesVisibleLabel || 'Notes'}
                </span>
                <textarea
                    className="inspect-flow-warn-inop-notes-input"
                    value={notes}
                    onChange={(event) => onNotesChange(event.target.value)}
                    rows={4}
                    placeholder="Describe the issue…"
                />
            </label>

            <div className="inspect-flow-media-actions">
                <label htmlFor={fileInputId} className="visually-hidden">
                    Add photo or video attachment
                </label>
                <input
                    ref={fileInputRef}
                    id={fileInputId}
                    type="file"
                    className="inspect-flow-media-file-input"
                    tabIndex={-1}
                    accept="image/jpeg,image/jpg,image/png,audio/wav,audio/mp3,video/*"
                    multiple
                    onChange={(event) => {
                        if (event.target.files?.length) {
                            onFilesSelected(event.target.files);
                        }
                        event.target.value = '';
                    }}
                />
                <Btn
                    variant="secondary-soft-outlined"
                    buttonClasses={clsx('inspect-flow-media-btn', 'inspect-flow-media-btn-photo')}
                    buttonIcon={<PhotoVideoIcon />}
                    buttonText="Add Photo / Video"
                    buttonOnClick={() => fileInputRef.current?.click()}
                />
                <Btn
                    variant="secondary-soft-outlined"
                    buttonClasses={clsx(
                        'inspect-flow-media-btn',
                        'inspect-flow-media-btn-audio',
                        (isRecording || isStarting) && 'is-active',
                    )}
                    buttonIcon={<MicrophoneIcon />}
                    buttonText={
                        isStarting ? 'Starting…' : isRecording ? 'End Recording' : 'Record Audio Note'
                    }
                    buttonDisabled={isStarting}
                    buttonOnClick={() => {
                        if (isRecordingStopped(recorderStatus)) {
                            onStartRecording();
                            return;
                        }
                        onStopRecording();
                    }}
                />
            </div>

            {audioUrl && (
                <div className="inspect-flow-audio-review">
                    <audio controls src={audioUrl} className="inspect-flow-audio-player">
                        <track kind="captions" />
                    </audio>
                    <Btn
                        variant="secondary-soft-outlined"
                        buttonSize="sm"
                        buttonText="Remove audio"
                        buttonOnClick={onRemoveAudio}
                    />
                </div>
            )}

            {photoVideoItems.length > 0 && (
                <ul className="inspect-flow-media-preview-list">
                    {photoVideoItems.map((item) => {
                        const file = item.data?.file;
                        const previewUrl = file ? URL.createObjectURL(file) : null;
                        const isVideo = file?.type.startsWith('video/');

                        return (
                            <li key={String(item.id as Key)} className="inspect-flow-media-preview-item">
                                {previewUrl &&
                                    (isVideo ? (
                                        <video src={previewUrl} className="inspect-flow-media-preview-thumb" controls />
                                    ) : (
                                        <img
                                            src={previewUrl}
                                            alt={item.name ? `Uploaded file: ${item.name}` : 'Uploaded attachment preview'}
                                            className="inspect-flow-media-preview-thumb"
                                        />
                                    ))}
                                <Btn
                                    variant="icon"
                                    ariaLabel={`Remove ${item.name}`}
                                    buttonIcon={<span aria-hidden="true">×</span>}
                                    buttonClasses="inspect-flow-media-remove-btn"
                                    buttonOnClick={() => onRemoveMediaItem(item)}
                                />
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};
